<template>
  <el-dialog
    :visible.sync="visible"
    title="知识库文章"
    width="820px"
    top="6vh"
    custom-class="doc-modal-dialog"
    @close="$emit('close')"
  >
    <div v-loading="loading" class="doc-wrap">
      <template v-if="!loading">
        <h2 class="doc-title">{{ doc.title || '（无标题）' }}</h2>
        <div v-if="tagList.length" class="doc-tags">
          <span v-for="t in tagList" :key="t" class="doc-tag">{{ t }}</span>
        </div>
        <p v-else class="doc-tags-empty muted">暂无标签</p>
        <div class="doc-divider" />
        <div
          v-if="contentHtml"
          ref="content"
          class="doc-content markdown-body"
          v-html="contentHtml"
          @click="onContentClick"
        />
        <p v-else class="doc-empty muted">暂无正文</p>
      </template>
    </div>
    <video-player-dialog :show="videoShow" :src="videoSrc" @close="closeVideo" />
    <image-lightbox
      :show="imageShow"
      :list="imageList"
      :start-index="imageIndex"
      @close="closeImage"
    />
  </el-dialog>
</template>

<script>
import { renderMarkdown } from '@/utils/markdown'
import { getVideoSrc, markVideoLinks } from '@/utils/media'
import VideoPlayerDialog from './VideoPlayerDialog.vue'
import ImageLightbox from './ImageLightbox.vue'

export default {
  name: 'DocModal',
  components: { VideoPlayerDialog, ImageLightbox },
  props: {
    show: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    doc: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      videoShow: false,
      videoSrc: '',
      imageShow: false,
      imageList: [],
      imageIndex: 0
    }
  },
  computed: {
    visible: {
      get() { return this.show },
      set(v) { if (!v) this.$emit('close') }
    },
    contentHtml() {
      return markVideoLinks(renderMarkdown(this.doc.content || ''))
    },
    tagList() {
      const t = this.doc.tags
      if (!t) return []
      if (Array.isArray(t)) return t.map(String).map(s => s.trim()).filter(Boolean)
      return String(t).split(/[,，]/).map(s => s.trim()).filter(Boolean)
    }
  },
  watch: {
    show(v) {
      if (!v) {
        this.closeVideo()
        this.closeImage()
      }
    }
  },
  methods: {
    /** 正文点击：图片放大查看；视频链接弹窗播放，不跳走 */
    onContentClick(e) {
      const target = e.target
      if (!target || !target.closest) return

      const img = target.closest('img')
      if (img) {
        const src = img.currentSrc || img.getAttribute('src') || ''
        if (!src) return
        const list = this.collectImages()
        const i = list.indexOf(src)
        this.imageList = list.length ? list : [src]
        this.imageIndex = i >= 0 ? i : 0
        this.imageShow = true
        return
      }

      const anchor = target.closest('a')
      if (!anchor) return
      const src = getVideoSrc(anchor)
      if (!src) return
      e.preventDefault()
      this.videoSrc = src
      this.videoShow = true
    },
    /** 正文里全部图片地址，供查看器左右切换 */
    collectImages() {
      const root = this.$refs.content
      if (!root) return []
      return Array.from(root.querySelectorAll('img'))
        .map(i => i.currentSrc || i.getAttribute('src') || '')
        .filter(Boolean)
    },
    closeVideo() {
      this.videoShow = false
      this.videoSrc = ''
    },
    closeImage() {
      this.imageShow = false
      this.imageList = []
      this.imageIndex = 0
    }
  }
}
</script>

<style lang="scss" scoped>
.doc-wrap {
  min-height: 120px;
  max-height: 72vh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 2px 8px;
}

.doc-title {
  margin: 0 0 14px;
  font-size: 20px;
  font-weight: 700;
  color: #0F2942;
  line-height: 1.45;
}

.doc-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.doc-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 13px;
  background: #E0F2FE;
  color: #0369A1;
  font-weight: 500;
}

.doc-tags-empty {
  margin: 0 0 4px;
  font-size: 13px;
}

.doc-divider {
  height: 1px;
  background: $line;
  margin: 16px 0 18px;
}

.doc-empty {
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
}

.doc-content {
  font-size: 15px;
  line-height: 1.85;
  color: #1E293B;
  max-width: 100%;
  overflow-x: auto;
}
</style>

<style lang="scss">
.doc-modal-dialog {
  max-width: calc(100vw - 32px);

  .el-dialog__body {
    padding: 16px 24px 24px;
    overflow: hidden;
  }

  .el-dialog__header {
    padding: 18px 24px 12px;
    border-bottom: 1px solid #E2E8F0;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #0F2942;
    }
  }
}

/* 正文图片可点击放大（v-html 内容需非 scoped） */
.doc-content img {
  cursor: zoom-in;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 0 0 3px rgba(26, 115, 184, 0.28);
  }
}

/* 正文视频链接：带播放标记，点击弹窗播放而非跳转 */
.doc-content a[data-video="1"] {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px 2px 8px;
  border-radius: 12px;
  background: rgba(26, 115, 184, 0.1);
  color: $blue;
  text-decoration: none;

  &::before {
    content: "\25B6";
    font-size: 9px;
    line-height: 1;
  }

  &:hover {
    background: rgba(26, 115, 184, 0.18);
  }
}

@media (max-width: 640px) {
  .doc-modal-dialog {
    width: calc(100vw - 24px) !important;
    margin-top: 4vh !important;
  }
}
</style>
